import React from 'react';

import { LoginComponentProps } from '../types/LoginFormData';

// Type definitions
export type OAuthHandler = () => void;
export type TSPLoginComponent = (props: LoginComponentProps) => JSX.Element;

// TSP Categories for better organization
export enum TSPCategory {
  OAUTH = 'OAuth Based',
  API_KEY = 'API Key Based',
  CREDENTIALS = 'Credentials Based',
  CUSTOM = 'Custom Configuration',
}

// Core auth handler interface
export interface OAuthHandlers {
  initiateOAuth: (providerKey: string) => void;
}

// OAuth provider configuration
export interface OAuthConfig {
  authUrl: string;
  requiredParams: string[];
  getRedirectUri: (linkState: string) => string;
  defaultScopes?: {
    default?: string;
    [serviceName: string]: string | undefined;
  };
  getScope?: (params: { serviceName: string }) => string;
  // Optional: Custom URL building behavior
  urlBuildingOptions?: {
    // Whether to avoid URL-encoding certain parameters (e.g., scopes with + characters)
    disableScopeEncoding?: boolean;
    // Whether this provider uses pre-configured redirect URIs (don't send redirect_uri param)
    usePreConfiguredRedirectUri?: boolean;
    // Custom URL building function for complex cases
    customUrlBuilder?: (params: {
      authUrl: string;
      clientID: string;
      scope?: string;
      redirectUri: string;
      linkState: string;
      requiredParams: string[];
    }) => string;
  };
  // Optional: OAuth callback payload customization
  callbackOptions?: {
    // Whether to use the API key as the authorization code (e.g., Bouncie)
    useApiKeyAsAuthorizationCode?: boolean;
    // API field mappings for the onboard endpoint
    apiFieldMappings?: {
      // Field name for the API key in the onboard payload (defaults to 'api_key')
      apiKeyField?: string; // Only specify if different from 'api_key'
      // Field name for the redirect URI in the onboard payload
      redirectUriField?: string; // e.g., 'motive_redirect_uri', 'bouncie_redirect_uri'
      // Additional custom fields that should be included in the payload
      additionalFields?: Record<string, string>; // e.g., { code: 'apiKey' } for Bouncie
    };
  };
}

// Interface for TSP configuration
export interface TSPConfig {
  key: string;
  name: string;
  category: TSPCategory;
  imageName?: string; // Optional custom image name if different from the name
  oauth?: OAuthConfig; // OAuth configuration for OAuth-based TSPs
  helpText: React.ReactNode;
  loginComponent: (
    props: LoginComponentProps,
    oAuthHandlers: OAuthHandlers
  ) => JSX.Element;
}

// Helper function to generate standard OAuth help text
export const createOAuthHelpText = (providerName: string) => (
  <>
    <p>
      {providerName} uses OAuth for secure access to your telematics data. You
      can connect by providing your {providerName} admin credentials (i.e., your
      username and password). You have two options:
    </p>
    <ol>
      <li>
        Save your credentials (recommended):
        <ul>
          <li>Enter your {providerName} admin username and password</li>
          <li>We'll store them securely so we can reconnect automatically</li>
        </ul>
      </li>
      <li>
        Skip saving credentials:
        <ul>
          <li>Check "No thanks, I'll re-authorize when needed"</li>
          <li>
            You'll manually re-authorize if the connection expires or needs
            updating
          </li>
        </ul>
      </li>
    </ol>
    <p>
      In both cases, you'll be redirected to {providerName}'s OAuth flow to
      grant account access. If you need help finding your credentials, use the
      resources linked above.
    </p>
  </>
);
